import { useForm, FormProvider } from '@xmanscript/useform';
import { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ScaleLoader } from 'react-spinners';
import { motion } from 'framer-motion';
import { LinkService } from '@/ui/_apiUrls';
import { authenticatedApi } from '@/api/config';
import FormControl from '../FormControl';
import ErrorLabel from '../FormControl/_lib/ErrorLabel';
import Button from '../Button';

// Updated imports for proper asset handling
import facebookIcon from '@/assets/icons/facebook.png';
import instaIcon from '@/assets/icons/insta.png';
import linkedinIcon from '@/assets/icons/linkedin.png';
import tiktokIcon from '@/assets/icons/tiktok.png';
import youtubeIcon from '@/assets/icons/youtube.png';
import whatsappIcon from '@/assets/icons/whats-app.png';
import mapsIcon from '@/assets/icons/maps.png';
import mailIcon from '@/assets/icons/mail.png';
import { useUser } from '@/contexts/UserContext/UserContext';

interface AddLinkDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  link?: Record<string, any> | null;
  onDelete?: (link: any) => void;
}

export default function AddLinkDrawer({ isOpen, onClose, link, onDelete }: AddLinkDrawerProps) {
  const queryClient = useQueryClient();
  const { user, updateUser } = useUser();

  // Define mutations before useForm to avoid use-before-define errors
  const postLinkMutation = LinkService.post({
    mutationParams: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['link'] });
        onClose();
      },
    },
  });

  const patchLinkMutation = LinkService.patch({
    mutationParams: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['link'] });
        onClose();
      },
    },
  });

  const { isLoading, isError } = postLinkMutation;

  const { isLoading: isPatchingLink, isError: isErrorPatching } = patchLinkMutation;

  // const postIcon = postIconMutation.mutate;

  const formMethods = useForm({
    formName: 'addLinkForm', // Removed Date.now() to prevent re-renders
    initialValues: {},
    submitHandler: async (vals) => {
      try {
        const {
          currentPacket: { name, icon, url },
        } = vals;

        const isGoogleReview = link?.type === 'google-review';
        const finalName = isGoogleReview ? 'Google Review' : name;
        // Use a default icon if no icon is selected
        const finalIcon = isGoogleReview ? { url: '/google.svg' } : icon || { url: null };

        // Handle local social media icons
        let iconUrl = null;
        if (finalIcon?.publicUrl) {
          // For local social media icons, use the publicUrl for the database
          iconUrl = finalIcon.publicUrl;
        } else if (finalIcon?.url && (finalIcon.url.startsWith('http://') || finalIcon.url.startsWith('https://'))) {
          iconUrl = finalIcon.url;
        }

        if (isGoogleReview) {
          // Handle Google Review links separately using the new API endpoint
          try {
            const response = await authenticatedApi.post('/api/v1/user/google-review-url/', {
              google_review_url: url,
            });

            // Invalidate both 'user' and 'user-detail' queries to ensure fresh data
            queryClient.invalidateQueries({ queryKey: ['user'] });
            queryClient.invalidateQueries({ queryKey: ['user-detail'] });
            // Also refresh the user context
            if (response.data?.result?.google_review_url) {
              updateUser({ google_review_url: response.data.result.google_review_url });
            }
            onClose();
          } catch (error) {

          }
        } else if (link && (link._id || link.id)) {
          // eslint-disable-next-line no-underscore-dangle
          const linkId = link._id || link.id;
          const patchData: Record<string, any> = { id: linkId, name: finalName, url: url };

          // Include thumbnail if we have an iconUrl
          if (iconUrl) {
            patchData.thumbnail = iconUrl;
          }

          patchLinkMutation.mutate(patchData);

          // If there's a new thumbnail file, upload it properly
          if (icon && icon.file && icon.local) {
            const fileToUpload = icon.file;
            const formData = new FormData();
            formData.append('file', fileToUpload);

            // First, upload the media file to get mediaId
            try {
              const mediaResponse = await authenticatedApi.post('/api/v1/media/', formData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                  // Remove the Accept header to avoid JSON parsing issues
                },
              });

              // Then, use the mediaId to set the thumbnail for the link
              if (mediaResponse.data && mediaResponse.data.id) {
                await authenticatedApi.post(`/api/v1/link/${linkId}/upload-thumbnail/`, {
                  mediaId: mediaResponse.data.id,
                });
              }

              queryClient.invalidateQueries({ queryKey: ['link'] });
            } catch (error) {
              console.error('Error uploading media:', error);
            }
          }
        } else {
          // Handle new link creation with thumbnail
          let newLinkId = null;
          
          // Create link first
          try {
            const postData: Record<string, any> = { name: finalName, url: url };
            
            // Include thumbnail if we have an iconUrl
            if (iconUrl) {
              postData.thumbnail = iconUrl;
            }
            
            const linkResponse = await authenticatedApi.post('/api/v1/link/', postData);
            newLinkId = linkResponse.data.result.id;
            
            // If there's a file to upload, upload it and set as thumbnail
            if (icon && icon.file && icon.local && newLinkId) {
              const formData = new FormData();
              formData.append('file', icon.file);

              // First, upload the media file to get mediaId
              const mediaResponse = await authenticatedApi.post('/api/v1/media/', formData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              });

              // Then, use the mediaId to set the thumbnail for the link
              if (mediaResponse.data && mediaResponse.data.id) {
                await authenticatedApi.post(`/api/v1/link/${newLinkId}/upload-thumbnail/`, {
                  mediaId: mediaResponse.data.id,
                });
              }
            }

            queryClient.invalidateQueries({ queryKey: ['link'] });
            onClose();
          } catch (error) {
            console.error('Error creating link with thumbnail:', error);
            queryClient.invalidateQueries({ queryKey: ['link'] });
            onClose();
          }
        }
      } catch (error: any) {
        throw new Error(error);
      }
    },
    validationSchema: (vals) => {
      const errors: Record<string, any> = {};
      const { name, url } = vals;
      if (!name && link?.type !== 'google-review') errors.name = 'Title is required.';
      if (!url) errors.url = 'Url is required.';
      // Temporarily remove icon requirement until media API is fixed
      // if (!icon) errors.icon = 'Icon is required.';
      return errors;
    },
    touchOnChange: true,
    persistValues: false, // Add this to prevent FormProvider warnings
  });

  const { register, onSubmitHandler, formState, setValues } = formMethods;

  useEffect(() => {
    if (!link) return;

    const isGoogleReview = link.type === 'google-review';
    // Only set thumbnail if it's a valid URL
    const thumbnailUrl =
      link.thumbnail && (link.thumbnail.startsWith('http://') || link.thumbnail.startsWith('https://'))
        ? link.thumbnail
        : null;

    setValues({
      name: isGoogleReview ? 'Google Review' : link.name,
      url: isGoogleReview ? (link.url && link.url.trim() !== '' ? link.url : user?.google_review_url || '') : link.url,
      icon: isGoogleReview
        ? { url: '/google.svg' }
        : thumbnailUrl
          ? { url: thumbnailUrl, _id: link._id || link.id }
          : null,
    });
  }, [link, setValues, user?.google_review_url]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      // Clear form when drawer closes
      try {
        setValues({});
      } catch (error) {
        // Silently handle form clearing errors
      }
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, setValues]);
  const iconsList = { result: [] }; // Mock empty result

  const defaultIcons = useMemo(
    () => [
      {
        _id: 'facebook',
        title: 'Facebook',
        localUrl: facebookIcon,
        publicUrl: `${import.meta.env.VITE_SITE_URL}/photos/facebook.png`,
        type: 'image',
      },
      {
        _id: 'instagram',
        title: 'Instagram',
        localUrl: instaIcon,
        publicUrl: `${import.meta.env.VITE_SITE_URL}/photos/insta.png`,
        type: 'image',
      },
      {
        _id: 'linkedin',
        title: 'LinkedIn',
        localUrl: linkedinIcon,
        publicUrl: `${import.meta.env.VITE_SITE_URL}/photos/linkedin.png`,
        type: 'image',
      },
      {
        _id: 'tiktok',
        title: 'TikTok',
        localUrl: tiktokIcon,
        publicUrl: `${import.meta.env.VITE_SITE_URL}/photos/tiktok.png`,
        type: 'image',
      },
      {
        _id: 'youtube',
        title: 'YouTube',
        localUrl: youtubeIcon,
        publicUrl: `${import.meta.env.VITE_SITE_URL}/photos/youtube.png`,
        type: 'image',
      },
      {
        _id: 'whatsapp',
        title: 'WhatsApp',
        localUrl: whatsappIcon,
        publicUrl: `${import.meta.env.VITE_SITE_URL}/photos/whats-app.png`,
        type: 'image',
      },
      {
        _id: 'maps',
        title: 'Maps',
        localUrl: mapsIcon,
        publicUrl: `${import.meta.env.VITE_SITE_URL}/photos/maps.png`,
        type: 'image',
      },
      {
        _id: 'mail',
        title: 'Mail',
        localUrl: mailIcon,
        publicUrl: `${import.meta.env.VITE_SITE_URL}/photos/mail.png`,
        type: 'image',
      },
    ],
    [],
  );

  // Memoize the icon options to prevent re-renders
  const iconOptions = useMemo(() => {
    // Include both server icons and local social media icons
    const serverIcons: any[] = (iconsList?.result || []).filter(
      (icon: any) =>
        icon.url && (icon.url.startsWith('http://') || icon.url.startsWith('https://') || icon.url.startsWith('data:')),
    );

    // Add link thumbnail if it's a valid URL
    let finalServerIcons: any[] = [...serverIcons];
    if (link && link.thumbnail && (link.thumbnail.startsWith('http://') || link.thumbnail.startsWith('https://'))) {
      if (!serverIcons.some((itm: any) => itm.url === link.thumbnail)) {
        finalServerIcons.push({ url: link.thumbnail });
      }
    }

    // Combine local social media icons with server icons
    return [...defaultIcons, ...finalServerIcons];
  }, [link, iconsList?.result, defaultIcons]);

  const handleDelete = (linkToDelete: any) => {
    if (onDelete) {
      onDelete(linkToDelete);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <FormProvider {...formMethods}>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="fixed inset-0 z-50 bg-black bg-opacity-50"
            onClick={handleBackdropClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleBackdropClick(e as any);
              }
            }}
          />

          {/* Drawer */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200,
              duration: 0.3,
            }}
            className="fixed bottom-0 left-0 right-0 z-50 flex justify-center"
          >
            <div className="mx-0 flex max-h-[90vh] w-full max-w-[768px] flex-col overflow-hidden rounded-tl-[28px] rounded-tr-[28px] border border-zinc-300 bg-white shadow-lg sm:mx-4">
              {/* Handle */}
              <div className="flex w-full items-center justify-center px-4 pt-6">
                <button
                  type="button"
                  onClick={handleClose}
                  className="h-1.5 w-[52px] cursor-pointer rounded-[20px] bg-zinc-300 transition-colors duration-200 hover:bg-zinc-400"
                  aria-label="Close drawer"
                />
              </div>

              {/* Form - Scrollable Content */}
              <div className="flex-1 overflow-y-auto px-4 pb-6">
                <div className="w-full space-y-4">
                  <FormControl
                    {...register('name')}
                    controlType="input"
                    label="Title for the link"
                    placeholder="Enter Title for the link"
                    className="text-base md:text-sm" // Prevent mobile zoom by ensuring minimum 16px font size
                  />

                  <FormControl 
                    {...register('url')} 
                    controlType="input" 
                    label="Link" 
                    placeholder="Enter Link" 
                    className="text-base md:text-sm" // Prevent mobile zoom by ensuring minimum 16px font size
                  />

                  {link?.name !== 'Google Review' && (
                    <FormControl
                      {...register('icon')}
                      controlType="iconSelector"
                      label="Choose Icon"
                      options={iconOptions}
                    />
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4">
                    {link ? (
                      <>
                        <Button
                          onClick={() => handleDelete(link)}
                          variant="secondary"
                          className="h-12 flex-1 rounded-lg border-2 border-sky-500 bg-white font-medium text-sky-500 transition-colors duration-200 hover:bg-sky-50"
                          type="button"
                        >
                          Delete
                        </Button>
                        <Button
                          onClick={onSubmitHandler}
                          variant={formState.hasError || formState.isSubmittingForm ? 'ghost' : 'primary'}
                          className="h-12 flex-1 rounded-lg bg-sky-500 font-medium text-white transition-colors duration-200 hover:bg-sky-600"
                          type="button"
                        >
                          Save Changes
                          {(isLoading || isPatchingLink) && <ScaleLoader height={10} color="white" />}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          onClick={handleClose}
                          variant="secondary"
                          className="h-12 flex-1 rounded-lg border-2 border-sky-500 bg-white font-medium text-sky-500 transition-colors duration-200 hover:bg-sky-50"
                          type="button"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={onSubmitHandler}
                          variant={formState.hasError || formState.isSubmittingForm ? 'ghost' : 'primary'}
                          className="h-12 flex-1 rounded-lg bg-sky-500 font-medium text-white transition-colors duration-200 hover:bg-sky-600"
                          type="button"
                        >
                          Save
                          {(isLoading || isPatchingLink) && <ScaleLoader height={10} color="white" />}
                        </Button>
                      </>
                    )}
                  </div>

                  {(formState.isSubmitionError || isError || isErrorPatching) && (
                    <ErrorLabel
                      message={
                        formState?.submitionError?.response?.data?.message || 'Something went wrong. Please try again.'
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </FormProvider>
  );
}
